import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { Drawer } from '@/components/feedback/drawer';
import { MultiSelectField, NumberField } from '@/components/forms';

import { toDefaults, toFilters } from '../filters';
import { CUSTOMER_STATUS_CHIP } from '../format';
import { customerFiltersSchema } from '../schemas';
import type { CustomerFilterFormValues, CustomerListFilters } from '../types';

const STATUS_OPTIONS = (
  Object.keys(CUSTOMER_STATUS_CHIP) as (keyof typeof CUSTOMER_STATUS_CHIP)[]
).map((value) => ({ value, label: CUSTOMER_STATUS_CHIP[value].label }));

export type CustomerFilterDrawerProps = {
  open: boolean;
  /** Last applied filters; the draft re-seeds from this on each open. */
  value: CustomerListFilters;
  onApply: (filters: CustomerListFilters) => void;
  onClose: () => void;
};

type BodyProps = Omit<CustomerFilterDrawerProps, 'open'>;

function FilterBody({ value, onApply, onClose }: BodyProps) {
  const form = useForm<CustomerFilterFormValues>({
    resolver: zodResolver(customerFiltersSchema),
    defaultValues: toDefaults(value),
    // onChange (vs Orders' onBlur) so the range error + the Apply-disable react live as the admin types.
    mode: 'onChange',
  });

  // Single source for the To>=From rule: the schema owns it, so disable Apply straight off the
  // validation state rather than re-deriving `from > to` here.
  const rangeInvalid = Boolean(form.formState.errors.deviceCountTo);

  const handleApply = form.handleSubmit((values) => {
    onApply(toFilters(values));
    onClose();
  });

  return (
    <Drawer
      open
      onClose={onClose}
      title="Filter"
      width={400}
      primaryText="Apply"
      onPrimary={handleApply}
      primaryDisabled={rangeInvalid}
      cancelText="Cancel"
      onCancel={onClose}
    >
      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend>Device Range</legend>
        <div style={{ display: 'flex', gap: 12 }}>
          <NumberField
            name="deviceCountFrom"
            control={form.control}
            aria-label="Device Count From"
            placeholder="From"
            min={0}
            precision={0}
            style={{ width: '100%' }}
          />
          <NumberField
            name="deviceCountTo"
            control={form.control}
            aria-label="Device Count To"
            placeholder="To"
            min={0}
            precision={0}
            style={{ width: '100%' }}
          />
        </div>
      </fieldset>

      <MultiSelectField
        name="statuses"
        control={form.control}
        label="Status"
        placeholder="Select"
        options={STATUS_OPTIONS}
      />
    </Drawer>
  );
}

export function CustomerFilterDrawer({ open, ...rest }: CustomerFilterDrawerProps) {
  if (!open) {
    return (
      <Drawer open={false} onClose={rest.onClose}>
        {null}
      </Drawer>
    );
  }
  // Unmount on close; remounts fresh on open so the draft re-seeds from the last applied `value`.
  return <FilterBody {...rest} />;
}
